import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import RelatedBand from '../components/RelatedBand';
import { HERO_MEDIA, SITE } from '../config/media';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
    ABS_URL,
} from '../lib/seo';

const BEAR_VALLEY_URL = 'https://bearvalleysolutions.com/centurion-matting';

// Frozen inline fallback - matches the current page JSX verbatim.
const IND_FALLBACK = {
    hero: {
        eyebrow: 'Indigenous Partnerships',
        headline: 'Manufacturer-direct matting through Indigenous First Nations partnerships.',
        body: 'In partnership with Bear Valley Solutions - an Indigenous First Nations economic development organization based in Western Canada.',
        image_url: HERO_MEDIA.aerialCorridor.src,
    },
    body: {
        lead: '',
        paragraphs: [
            'Centurion Matting Products partners with Bear Valley Solutions, an Indigenous First Nations economic development organization that matches Tier 1 service providers with First Nations partners across Western Canada. Through this partnership, Centurion\u2019s manufacturer-direct ground protection - built from coastal Hem-Fir at our Chemainus, BC mill - reaches projects through Indigenous-aligned procurement, employment, and joint-venture channels.',
            'We integrate Indigenous values, traditions, and protocols throughout our matting operations: respecting community sovereignty over the lands our mats deploy on, supporting First Nations participation in the matting supply chain, and ensuring ground-protection considerations are designed into every project phase from spec to recovery.',
        ],
    },
    principles: {
        items: [
            {
                title: 'Employment opportunities',
                body: 'For Indigenous community members across the matting supply chain - mat handling, transport, field service, and on-site operations.',
            },
            {
                title: 'Procurement partnerships',
                body: 'Manufacturer-direct mat orders routed through Indigenous-aligned procurement channels, with the short supply chain and visible margin that Manufacturer Direct enables.',
            },
            {
                title: 'Joint-venture pathways',
                body: 'Formal joint-venture frameworks that match Centurion\u2019s manufacturing capability with First Nations community priorities - a structural stake in the supply, not a one-off sale.',
            },
        ],
    },
    faq: {
        items: [
            {
                q: 'Who is Bear Valley Solutions?',
                a: 'Bear Valley Solutions is an Indigenous First Nations economic development organization based in Western Canada. They match Tier 1 service providers and manufacturers with First Nations partners through their Partner Program.',
            },
            {
                q: 'What is the Centurion-Bear Valley partnership?',
                a: 'Centurion Matting partners with Bear Valley Solutions to bring manufacturer-direct ground protection to projects through Indigenous-aligned procurement, employment, and joint-venture channels. Through the partnership, Centurion mats reach First Nations-aligned project work across British Columbia, Alberta, and Saskatchewan.',
            },
            {
                q: 'How do Indigenous partnerships work in matting supply?',
                a: 'Project-level engagement is led by the priorities of the community and the existing relationship between project owner and Nation. Bear Valley Solutions operates as the matchmaker between Tier 1 service providers like Centurion and First Nations partners, with measurable economic outcomes built into each engagement.',
            },
            {
                q: 'How does Centurion integrate Indigenous values into operations?',
                a: 'By respecting community sovereignty over the lands our mats deploy on, supporting First Nations participation in the matting supply chain (employment + procurement), and ensuring ground-protection considerations are designed into every project phase - from initial spec through manufacture, deployment, and mat recovery.',
            },
        ],
    },
};

export default function IndigenousPartnerships() {
    const content = usePageContent('partnerships.indigenous', IND_FALLBACK);
    const hero = content.hero || IND_FALLBACK.hero;
    const body = content.body || IND_FALLBACK.body;
    const principles = (content.principles?.items?.length ? content.principles.items : IND_FALLBACK.principles.items);
    const faqs = (content.faq?.items?.length ? content.faq.items : IND_FALLBACK.faq.items);

    return (
        <>
            <SEO
                title="Indigenous Partnerships - Bear Valley Partner Program"
                description={`${SITE.name} partners with Bear Valley Solutions, an Indigenous First Nations economic development organization, to deliver manufacturer-direct timber matting through Indigenous-aligned procurement, employment, and joint-venture channels across Western Canada.`}
                cmsSlug="seo.partnerships.indigenous"
                path="/partnerships/indigenous"
                image={HERO_MEDIA.aerialCorridor.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Partnerships', path: '/partnerships' },
                        { name: 'Indigenous', path: '/partnerships/indigenous' },
                    ]),
                    faqJsonLd(faqs),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'AboutPage',
                        name: 'Centurion Matting - Indigenous Partnerships',
                        url: ABS_URL('/partnerships/indigenous'),
                        description:
                            'Centurion Matting Products partners with Bear Valley Solutions, an Indigenous First Nations economic development organization, to deliver manufacturer-direct ground protection through Indigenous-aligned employment, procurement, and joint-venture channels across Western Canada.',
                        about: {
                            '@type': 'Organization',
                            name: 'Bear Valley Solutions',
                            url: 'https://bearvalleysolutions.com',
                            description:
                                'Indigenous-focused economic development organization partnering with Tier 1 manufacturers and service providers to facilitate Indigenous community participation through employment, procurement, and joint-venture opportunities.',
                        },
                    },
                ]}
            />

            <HeroPlaceholder
                mediaKey="aerialCorridor"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                {(body.paragraphs || []).map((p, i) => (
                    <p
                        key={i}
                        className={i === 0
                            ? 'mt-8 font-sans text-lg leading-relaxed text-brand-text'
                            : 'mt-6 font-sans text-base leading-relaxed text-brand-muted'}
                    >
                        {p}
                    </p>
                ))}
            </section>

            {/* Strategic Partner - Bear Valley Solutions */}
            <section
                className="border-y border-brand-hairline bg-brand-surface"
                data-testid="bvs-partner-section"
            >
                <div className="mx-auto flex max-w-4xl flex-col items-center px-6 py-16 md:py-20">
                    <span className="eyebrow">Strategic Partner</span>
                    <a
                        href="https://bearvalleysolutions.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Bear Valley Solutions - opens in a new tab"
                        data-testid="bvs-partner-logo-link"
                        className="group mt-8 inline-flex flex-col items-center gap-5 transition-opacity duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-4 focus-visible:ring-offset-brand-surface"
                    >
                        <span className="block bg-white p-6 md:p-8">
                            <img
                                src="/api/uploads/hero/8d591837304f45beb74fdc710e20f429.webp"
                                alt="Bear Valley Solutions"
                                loading="lazy"
                                width="320"
                                height="auto"
                                className="block w-[240px] md:w-[320px]"
                                data-testid="bvs-partner-logo"
                            />
                        </span>
                        <span className="inline-flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-[0.18em] text-brand-text transition-colors duration-150 group-hover:text-brand-gold">
                            Bear Valley Solutions
                            <ArrowUpRight size={14} aria-hidden="true" />
                        </span>
                    </a>
                    <p className="mt-5 max-w-xl text-center font-sans text-sm leading-relaxed text-brand-muted">
                        Indigenous First Nations economic development organization. Centurion&apos;s partnership channel for First Nations procurement, employment, and joint-venture pathways across Western Canada.
                    </p>
                </div>
            </section>

            {/* What the Partnership Delivers */}
            <section className="border-y border-brand-hairline bg-brand-surface" data-eyebrow-tone="blue">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">What the Partnership Delivers</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Three structural channels for Indigenous participation.
                    </h2>
                    <div className="mt-12 grid gap-px bg-brand-hairline md:grid-cols-3" data-testid="indigenous-pillars">
                        {principles.map((p, i) => (
                            <div key={p.title} className="bg-brand-surface p-6 md:p-8">
                                <span className="font-display text-xs font-bold tabular-nums tracking-[0.2em] text-brand-gold">
                                    0{i + 1}
                                </span>
                                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-brand-text">
                                    {p.title}
                                </h3>
                                <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works + AEO Callout */}
            <section className="mx-auto max-w-7xl grid gap-12 px-6 py-20 md:grid-cols-[1.1fr_1fr] md:gap-16 md:px-10 md:py-28">
                <div>
                    <span className="eyebrow">How It Works</span>
                    <h2 className="h-display mt-6 text-2xl sm:text-3xl">
                        Matchmaker between Tier 1 manufacturers and First Nations partners.
                    </h2>
                    <p className="mt-5 font-sans text-base leading-relaxed text-brand-muted">
                        Bear Valley Solutions operates the Indigenous First Nations Partner Program - matching Tier 1 service providers like Centurion with First Nations communities. The program creates measurable economic outcomes that honor Indigenous sovereignty while delivering ground protection at project scale.
                    </p>
                </div>
                <AeoCallout
                    testId="aeo-indigenous"
                    q="About Bear Valley Solutions"
                    a="Bear Valley Solutions is an Indigenous-focused economic development organization based in Western Canada. They partner with Tier 1 manufacturers and service providers (including Centurion Matting) to facilitate Indigenous community participation through employment, procurement, and joint-venture opportunities."
                />
            </section>

            {/* Communities Across Western Canada */}
            <section className="border-y border-brand-hairline bg-brand-surface">
                <div className="mx-auto max-w-4xl px-6 py-20 md:py-24">
                    <span className="eyebrow">Communities Across Western Canada</span>
                    <p className="mt-8 font-sans text-base leading-relaxed text-brand-muted">
                        Centurion supplies ground protection on projects across British Columbia, Alberta, and Saskatchewan. Where partnership frameworks exist, we work inside them; where they don’t yet, we’re ready to participate in building them - with Bear Valley Solutions as our Indigenous partnership channel.
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Working with First Nations partners through Bear Valley.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={faqs} testId="faq-indigenous" />
                </div>
            </section>

            <RelatedBand
                heading="Adjacent partnerships."
                items={[
                    { to: '/partnerships/procurement', eyebrow: 'Procurement', title: 'Procurement Partnerships', body: 'Manufacturer-direct wholesale for contractors and matting service companies.' },
                    { to: '/partnerships/sustainability', eyebrow: 'Sustainability', title: 'Sustainability', body: 'Renewable timber, reduced ground impact, multi-deployment lifecycle.' },
                    { to: '/projects', eyebrow: 'Projects', title: 'All Projects', body: 'Pipeline, infrastructure, wind, and partnerships in one view.' },
                ]}
            />

            {/* Custom CTA band - primary internal + secondary external (Bear Valley) */}
            <section
                className="border-t border-brand-hairline bg-brand-elevated"
                data-testid="indigenous-cta-band"
            >
                <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-16 md:flex-row md:items-center md:justify-between md:px-10 md:py-20">
                    <div className="max-w-2xl">
                        <span className="eyebrow">Partnership Inquiries</span>
                        <h2 className="h-display mt-5 text-3xl sm:text-4xl">
                            Inquire about a partnership project.
                        </h2>
                        <p className="mt-4 font-sans text-base leading-relaxed text-brand-muted">
                            Manufacturer-direct ground protection through Indigenous-aligned channels - reach out and we’ll connect your project with the right partnership pathway.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <Link
                            to="/contact?topic=partnership"
                            data-testid="indigenous-cta-primary"
                            className="inline-flex items-center justify-center bg-brand-gold px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-brand-gold-hover"
                        >
                            Inquire About a Partnership Project
                        </Link>
                        <a
                            href={BEAR_VALLEY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="indigenous-cta-bear-valley"
                            className="inline-flex items-center justify-center gap-2 border border-brand-text px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-text transition-colors duration-150 hover:border-brand-gold hover:text-brand-gold"
                        >
                            Learn More About Bear Valley
                            <ArrowUpRight size={14} />
                        </a>
                    </div>
                </div>
            </section>

            {/* Fallback hidden CtaBand removed - replaced by the custom block above. */}
            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Spec your project. We'll respond within a business day."
                body={'Pipeline, infrastructure, wind, partnerships - Manufacturer Direct from our Chemainus, BC mill.'}
                cta={{ to: '/quote', label: 'Get a Manufacturer Direct Quote' }}
                secondary={{ to: '/contact', label: 'Talk to Sales' }}
            />
        </>
    );
}
